/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*/
import { CurrencyPipe, DatePipe, NgClass, NgTemplateOutlet, PercentPipe } from '@angular/common';
import { Component, inject } from '@angular/core';
import { MatCardModule } from '@angular/material/card';
import { Billing, BillingItem } from '@src/app/model/billing';
import { TextEditComponent } from '@src/app/shared/simpleFields/text-edit/text-edit.component';
import { BillingService } from '@src/app/modules/budget/billing.service';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { DateTimeItemComponent } from '@src/app/shared/simpleFields/date-time-item/date-time-item.component';
import { CurrencyItemComponent } from '@src/app/shared/simpleFields/currency-item/currency-item.component';
import { NavRoute } from '@src/app/shared/NavRoute';
import { NavigateItemComponent } from '@src/app/shared/simpleFields/navigate-item/navigate-item.component';
import { ScheduleService } from '@src/app/modules/budget/schedule.service';
import { Unit } from '@src/app/model/unit';
import { combineLatest, of } from 'rxjs';
import { BCodeService } from '@src/app/modules/budget/bcode.service';
import { MyUnitService } from '@src/app/modules/unit/my-unit.service';
import { Schedule } from '@src/app/model/schedule';
import { BCode } from '@src/app/model/bcode';
import { concatMap, map } from 'rxjs/operators';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatDialog } from '@angular/material/dialog';
import { ShowUnitBillingComponent } from '@src/app/modules/budget/billing-page/show-unit-billing/show-unit-billing.component';
import { MatTooltipModule } from '@angular/material/tooltip';
import { InvoicingActionComponent } from '@src/app/modules/budget/billing-page/invoicing-action/invoicing-action-component';
import { UserNameComponent } from '@src/app/modules/user/user-name/user-name.component';
import { FromNowPipe } from '@src/app/shared/pipes/from-now.pipe';
import { MailMerge } from '@src/app/model/mailMerge';
import { DateHelper } from '@src/app/shared/dateHelper';
import { Cycle } from '@src/app/model/cycle';
import { ConfirmDialogService } from '@src/app/shared/dialogs/confirmDialog';
import { AttachmentGridComponent } from '@src/app/shared/form/file/attachment-grid/attachment-grid.component';
import { CtlHolderComponent } from "@src/app/shared/form/ctl-holder/ctl-holder.component";
import { SchedulePageNavRoute } from '@src/app/routes/schedule-page-nav';
import { BCodePageNavRoute } from '@src/app/routes/bcode-page-nav';
import { BudgetingPageNavRoute } from '@src/app/routes/app-router';
import { UnitPageNavRoute } from '@src/app/routes/unit-page';
import { PersonPageNavRoute } from '@src/app/routes/person-page-nav';
import { ArPageNavRoute } from '@src/app/routes/ar-page-nav';
import { OmcSettingsService } from '@src/app/pages/omc-settings/omc-settings.service';
import { OmcSettings } from '@src/app/model/OmcSettings';
import { VatRateService } from '@src/app/modules/txn/VATRate.service';

@Component({
    selector: 'app-billing-detail',
    imports: [TextEditComponent, MatCardModule, DateTimeItemComponent, RouterLink,
        MatButtonModule, MatIconModule, CurrencyItemComponent, NavigateItemComponent, NgClass, NgTemplateOutlet,
        CurrencyPipe, MatTooltipModule, UserNameComponent, FromNowPipe, DatePipe, PercentPipe, CtlHolderComponent],
    templateUrl: './billing-detail.component.html',
    styleUrl: './billing-detail.component.scss'
})
export class BillingDetailComponent {
    billing: Billing = new Billing();
    loaded = false;
    billingSvc = inject(BillingService)
    activatedRoute = inject(ActivatedRoute);
    scheduleSvc = inject(ScheduleService);
    bCodeSvc = inject(BCodeService);
    myUnitSvc = inject(MyUnitService);
    dialog = inject(MatDialog);
    cds = inject(ConfirmDialogService);
    omcSettingsSvc = inject(OmcSettingsService);
    vatRateSvc = inject(VatRateService);

    units: Unit[] = [];
    schedules: Schedule[] = [];
    bCodes: BCode[]= [];

    vatRegistered = false;

    daysSinceStart = 0;
    pctExpected = 0;
    pctAchieved = 0;
    collectionStatus : 'good' | 'bad' | 'mediocre' = 'good';
    toBeBilled = false;
    toBeApproved = false;
    beingBilled = false;

    get invNavRoute() : NavRoute { return ArPageNavRoute}

    get budgetNav(): string {
        return BudgetingPageNavRoute.getIdUrl(this.billing.cycleId);
    }

    constructor() {

        // A real combine latest, uses the activated route to get the route change to get billing...
        combineLatest([
            this.scheduleSvc.get(true),
            this.bCodeSvc.get(true),
            this.myUnitSvc.get(),
            this.omcSettingsSvc.getOne<OmcSettings>('omcSettings', null).pipe( concatMap(
                omcSettings => {
                    if (this.vatRateSvc.vatRegistered(omcSettings)) {
                        this.vatRegistered = true;
                        return this.vatRateSvc.getVatRates().pipe( map( () => omcSettings))
                    }
                    return of(omcSettings)
                }
            )),             
            this.activatedRoute.params.pipe( concatMap(params => this.billingSvc.getOne(params.itemId, null)))
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        ]).subscribe(([schedules, bcodes, units, _omcSettings, billing]) => {
            this.units = units as Unit[];
            this.schedules = (schedules as Schedule[]);
            this.bCodes = (bcodes as BCode[]);
            if (billing) {
                this.loadBilling(billing);
                this.loaded = true;
            }
        });
    }

    getBCodeUrl(bCodeId) {
        return BCodePageNavRoute.getIdUrl(bCodeId);
    }

    getScheduleUrl(scheduleId) {
        return SchedulePageNavRoute.getIdUrl(scheduleId);
    }

    loadBilling(billing: Billing = null) {
        if (billing) this.billing = billing;
        if (!this.billing) return;

        this.toBeApproved = false;
        this.toBeBilled = false;
        this.beingBilled = false
        if (this.billing.statusId === Cycle.statusIds.PROPOSED) {
            this.toBeApproved = true;
        } else if (this.billing.statusId === Cycle.statusIds.ACCEPTED) {
            this.toBeBilled = true;
        } else if (this.billing.statusId === Cycle.statusIds.BILLING) {
            this.beingBilled = true;
        }
        this.billing.mailMerges.forEach (mm => {
            if (mm.attachments?.length > 0) {
                mm['attachField'] = AttachmentGridComponent.make(this.billingSvc);
                mm['attachField'].override({showSimple: true, readonly: true, showGrid: false});
                mm['attachField'].setupControl();
                mm['attachField'].setValue(mm, true);
            }
        })
        for (const bi of this.billing.items) {
            const bibc = this.bCodes.find(o => o.id === bi.bCodeId);
            if (bibc) {
                bi.bCodeName = bibc.name;
                if (this.vatRegistered) {
                    bi.vatCode = bibc.vatCode
                    bi.vatRate = this.vatRateSvc.getRate(bibc.vatCode, this.billing.cycle.from )
                    bi.vatAmount = bi.total * bi.vatRate / 100
                }
            }
        }
        this.calcUnitAmounts();
    }

    getUnitsLink(mm: MailMerge) {
        return UnitPageNavRoute.getListRouterLink({ mergeId: mm.id });
    }

    getPeopleLink(mm: MailMerge) {
        return PersonPageNavRoute.getListRouterLink({ mergeId: mm.id });
    }

    calcCollectedPcts() {
        this.daysSinceStart = DateHelper.isoDaysSince(this.billing.cycle.from);
        this.pctExpected = this.daysSinceStart > 365 ? 1 : (this.daysSinceStart <= 0 ? 0 : this.daysSinceStart / 365);
        const expected = this.billing.billedTotal * this.pctExpected
        const collected = this.billing.billedTotal - this.billing.outstandingTotal;
        this.pctAchieved = collected / this.billing.billedTotal;
        if ( collected > (expected * .99)) {
            this.collectionStatus = 'good'
        } else if (collected > (expected * .8)){
            this.collectionStatus = 'mediocre';
        } else {
            this.collectionStatus = 'bad';
        }
    }
    calcUnitAmounts() {
        this.calcCollectedPcts();
        this.billing.status = Cycle.statuses.find( o => o.id === this.billing.statusId);
        this.billing['toBill'] = 0;
        this.billing['wasBilled'] = 0;
        for (const bi of this.billing.items) {
            this.billing['toBill'] += bi.total;
            this.billing['wasBilled'] += bi.billed;
        }
        for (const u of this.units) {
            let uTotal = 0;
            //for (const s of this.schedules) {
            for (const bi of this.billing.items) {
                const uPortion = this.getAmount(u, bi);
                u[this.getKey(bi)] = uPortion;
                uTotal += uPortion;
            }
            for (const bi of this.billing.items) {
                bi['unit-' + u.id] = this.getAmount(u, bi)
            }
            u['total'] = uTotal;

            this.billing['total-' + u.id] = uTotal;
        }
    }

    showUnit() {
        const dialogRef = this.dialog.open(ShowUnitBillingComponent);
        return dialogRef.afterClosed().subscribe((u: Unit) => {
            if (u) {
                this.units.push(u);
                this.loadBilling();
            }
        });
    }
    addCommunication(mailMerge:MailMerge = null) {
        this.dialog.open(InvoicingActionComponent, {data: {billing: this.billing, mailMerge}}).afterClosed().subscribe ( b => {
            if (b) {
                this.loadBilling(b);
            }
        });
    }

    getKey(bi: BillingItem) {
        return (bi.scheduleId as string) + (bi.bCodeId as string);
    }

    getAmount(unit: Unit, charge: BillingItem) {
        //const charge = billing.items.find(s => s.scheduleId === schedule.id);
        if (charge) {
            return charge.total * this.unitPercent(unit, charge);
        } else {
            console.warn('No charge for schedule', { unit, charge });
            return 0;
        }
    }

    unitPercent(unit: Unit, bi: BillingItem) {
        const portion = unit.portions.find(p => p.scheduleId === bi.scheduleId);
        return portion ? portion.percent : 0;
    }

    billOut() {
        const title = $localize`Billout Now`;
        const msg = $localize`Issue service charges to members now WARNING THIS CANNOT BE UNDONE?`;
        this.cds.open(title,msg, () => {
            this.billingSvc.billout(this.billing).subscribe( x => {
                if (x) {
                    this.loadBilling(x);
                }
            })
        });
    }
}
